import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Spin, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  type CustomElement,
  type CustomElementType,
  customElementsApi,
} from '@/api/customElements';
import { useCurrentUserEmail } from '@/auth/useCurrentUserEmail';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { PageHeader } from '@/components/PageHeader';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

const { Text } = Typography;

const KIND_COLOR: Record<CustomElementType, string> = {
  Field: 'blue',
  Section: 'purple',
  Step: 'orange',
};

/**
 * Library of every saved custom field/section/step — the originator's own elements
 * first (then a divider, then everyone else's), with search. Author-only Delete;
 * everyone can navigate in to view, and only the author can save changes once there
 * (the editor page re-checks authorship server-side on every Save).
 */
export function CustomElementSelectorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const myEmail = useCurrentUserEmail();

  const [elements, setElements] = useState<CustomElement[] | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const list = await customElementsApi.list();
      setElements(list);
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.editLoadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (elements ?? []).filter(
      (el) =>
        needle === '' ||
        el.nameEn.toLowerCase().includes(needle) ||
        el.nameFr.toLowerCase().includes(needle),
    );
  }, [elements, search]);

  const mine = filtered.filter((el) => myEmail !== null && el.author === myEmail);
  const others = filtered.filter((el) => !(myEmail !== null && el.author === myEmail));

  const handleDelete = async (el: CustomElement) => {
    try {
      await customElementsApi.remove(el.id);
      message.success(t('workflow.customElements.deleted'));
      await load();
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.deleteFailed'));
    }
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
      <PageHeader
        title={t('workflow.customElements.selectorTitle')}
        onBack={() => navigate('/originator/dashboard')}
      />
      <Text style={{ display: 'block', margin: '8px 0 20px', color: '#475467' }}>
        {t('workflow.customElements.selectorSubtitle')}
      </Text>

      <Input
        placeholder={t('workflow.customElements.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        suffix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
        style={{ marginBottom: 16, maxWidth: 360 }}
      />

      {elements === null ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <Empty description={t('workflow.customElements.noneFound')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {mine.length > 0 && (
            <ElementGroup
              headerKey="workflow.customElements.mineHeader"
              items={mine}
              myEmail={myEmail}
              onDelete={handleDelete}
            />
          )}
          {mine.length > 0 && others.length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
          )}
          {others.length > 0 && (
            <ElementGroup
              headerKey="workflow.customElements.othersHeader"
              items={others}
              myEmail={myEmail}
              onDelete={handleDelete}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ElementGroup({
  headerKey,
  items,
  myEmail,
  onDelete,
}: {
  headerKey: string;
  items: CustomElement[];
  myEmail: string | null;
  onDelete: (el: CustomElement) => Promise<void>;
}) {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#6b7280',
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        {t(headerKey)}
      </Text>
      {items.map((el) => (
        <ElementCard
          key={el.id}
          el={el}
          canDelete={myEmail !== null && el.author === myEmail}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function ElementCard({
  el,
  canDelete,
  onDelete,
}: {
  el: CustomElement;
  canDelete: boolean;
  onDelete: (el: CustomElement) => Promise<void>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const stopPropagation: React.MouseEventHandler = (e) => e.stopPropagation();

  return (
    // A row that navigates IS a button (WCAG 2.1.1). It was reachable only by
    // mouse: no tab stop, no Enter/Space, and nothing announcing it as
    // actionable. role + tabIndex + key handling make it a real control.
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/originator/custom-elements/${el.id}/edit`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/originator/custom-elements/${el.id}/edit`);
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        background: '#fff',
        cursor: 'pointer',
        boxShadow: hovered ? '0 2px 8px rgba(0,0,0,0.10)' : 'none',
        transition: 'box-shadow 0.15s ease',
      }}
    >
      <Tag color={KIND_COLOR[el.type]} style={{ margin: 0 }}>
        {t(`workflow.customElements.kind${el.type}`)}
      </Tag>
      <Text strong style={{ fontSize: 13, color: '#111827', flex: 1, minWidth: 0 }} ellipsis>
        {el.nameEn}
      </Text>
      <Text style={{ fontSize: 12, color: '#6b7280', flexShrink: 0, width: 200 }} ellipsis>
        {el.author}
      </Text>
      <Text style={{ fontSize: 12, color: '#6b7280', flexShrink: 0, width: 110 }}>
        {new Date(el.updatedAt).toLocaleDateString()}
      </Text>
      {/* Swallows the row's navigate-on-click so the delete popover doesn't also
          open the editor. Pointer-only by nature — the keyboard path never
          bubbles through here because the button inside handles its own keys. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <span onClick={stopPropagation}>
        <ConfirmPopover
          title={t('workflow.customElements.deleteConfirmTitle')}
          description={t('workflow.customElements.deleteConfirmDesc')}
          onConfirm={() => onDelete(el)}
          disabled={!canDelete}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={!canDelete}
            aria-label={t('workflow.customElements.deleteConfirmTitle')}
          />
        </ConfirmPopover>
      </span>
    </div>
  );
}
